import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { EXTERNAL_SUPABASE_PUBLISHABLE_KEY } from "@/integrations/external-supabase/client";
import { PINTEREST_AUTH_URL } from "@/hooks/usePinterest";
import { consumePinterestOAuth } from "@/lib/pinterestOAuth";
import { reportError } from "@/lib/reportError";

/**
 * How long to wait before asking check_status a second time.
 *
 * The first ask failed the same way the exchange did — a dropped connection on
 * a flaky phone — so it is retried once rather than treated as an answer. One
 * retry, not a loop: she is sitting on a spinner, and if two asks 2s apart both
 * die on transport the honest thing is to say so.
 */
const CHECK_RETRY_DELAY_MS = 2_000;

/**
 * What we managed to learn about her connection after the exchange reply was
 * lost. `check_failed` is deliberately NOT folded into `not_connected` — "we
 * could not find out" and "she is not connected" are different facts and only
 * one of them would justify telling her it did not work.
 */
type ConnectionVerdict = "connected" | "not_connected" | "check_failed";

/**
 * #1073 — Pinterest sends her back here with ?code=&state=.
 *
 * Mirrors KrogerCallback.tsx, plus one thing Kroger's flow never had: the
 * `state` check, BEFORE the exchange. The redirect URI itself is pinned on the
 * server, so nothing here decides where tokens are sent.
 */
export default function PinterestCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { session, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<"connecting" | "success" | "error">("connecting");
  const [errorMessage, setErrorMessage] = useState("");
  // #1867 — the heading is a claim about her connection, so it must only say
  // what we actually established. Every pre-existing error screen keeps
  // "Couldn't connect"; only the lost-reply verdict `check_failed` changes it,
  // because that is the one case where nothing proved she is disconnected.
  const [errorHeading, setErrorHeading] = useState("Couldn't connect");
  const [returnPath, setReturnPath] = useState("/");
  // The state is single-use. Without this, React re-running the effect (auth
  // settling, StrictMode) would consume it a second time and fail a good login.
  const started = useRef(false);

  const code = searchParams.get("code");
  const state = searchParams.get("state");
  const errorParam = searchParams.get("error");

  useEffect(() => {
    if (started.current) return;

    if (errorParam) {
      started.current = true;
      const { returnPath: back } = consumePinterestOAuth(state, window.location.origin);
      setReturnPath(back);
      // Pinterest's error_description is not shown: it is their text, not ours.
      setErrorMessage("Pinterest didn't connect. You can try again anytime.");
      setStatus("error");
      return;
    }

    if (!code) {
      started.current = true;
      const { returnPath: back } = consumePinterestOAuth(state, window.location.origin);
      navigate(back, { replace: true });
      return;
    }

    // The exchange needs her session; this page can mount before it resolves.
    if (authLoading) return;
    started.current = true;

    const { stateOk, returnPath: back } = consumePinterestOAuth(state, window.location.origin);
    setReturnPath(back);
    if (!stateOk) {
      setErrorMessage("That link didn't come from this device. Start connecting again from Kin.");
      setStatus("error");
      return;
    }
    if (!session?.access_token) {
      setErrorMessage("Your session expired. Sign back in and try connecting again.");
      setStatus("error");
      return;
    }

    let cancelled = false;
    // One object, used by the exchange AND by the check below. "Same headers as
    // the exchange" is then structurally true rather than a comment that can
    // drift — pinned by pinterestCallbackLostReply.test.tsx.
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${session.access_token}`,
      apikey: EXTERNAL_SUPABASE_PUBLISHABLE_KEY,
    };

    /**
     * Ask the server whether her tokens are actually stored.
     *
     * Throws on transport failure, exactly like the exchange does, so the
     * caller can tell "no answer" from "an answer of no". A non-ok response is
     * an answer of a third kind: check_status 500s when the lookup itself
     * failed, and that is not evidence she is disconnected.
     */
    const askCheckStatus = async (): Promise<ConnectionVerdict> => {
      const resp = await fetch(PINTEREST_AUTH_URL, {
        method: "POST",
        headers,
        body: JSON.stringify({ action: "check_status" }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) return "check_failed";
      return data.connected === true ? "connected" : "not_connected";
    };

    (async () => {
      try {
        const resp = await fetch(PINTEREST_AUTH_URL, {
          method: "POST",
          headers,
          body: JSON.stringify({ action: "exchange", code }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (resp.ok && data.success === true) {
          setStatus("success");
          setTimeout(() => {
            if (!cancelled) navigate(back, { replace: true });
          }, 900);
        } else {
          setErrorMessage(
            data.code === "not_pro"
              ? "Pinterest boards are part of Kin Pro 💜"
              : "Something went wrong on our end. Try connecting again.",
          );
          setStatus("error");
        }
      } catch (err) {
        if (cancelled) return;

        // ⚠️ Sentry JAVASCRIPT-REACT-32 — a transport failure HERE does not mean
        // the exchange did not happen. On Oct 19 the POST reached pinterest-auth
        // and her pinterest_tokens row was written at 13:09:47; only the reply
        // was lost (WebKit "Load failed"). She was connected and this screen
        // told her she wasn't.
        //
        // The one thing we must NOT do is retry the exchange: the authorization
        // code is single-use, so a second POST fails at Pinterest even though
        // the first one succeeded — turning a cosmetic wrong message into a
        // real failure. So we ask a different, idempotent question instead.
        // check_status is never Pro-gated and never refreshes into a lie.
        let verdict: ConnectionVerdict;
        try {
          verdict = await askCheckStatus();
        } catch {
          // Same failure as the exchange — one retry, then we stop guessing.
          await new Promise((resolve) => setTimeout(resolve, CHECK_RETRY_DELAY_MS));
          if (cancelled) return;
          try {
            verdict = await askCheckStatus();
          } catch {
            verdict = "check_failed";
          }
        }
        if (cancelled) return;

        if (verdict === "connected") {
          // Identical to the normal success path above, deliberately: from her
          // side nothing went wrong, so nothing should look like it did.
          setStatus("success");
          setTimeout(() => {
            if (!cancelled) navigate(back, { replace: true });
          }, 900);
          return;
        }

        // Only now is the error screen honest — and only now do we report, so
        // the Sentry issue stops collecting the self-healing population that
        // made up most of it.
        reportError("Pinterest connect: exchange reply lost", err, {
          tags: { area: "pinterest-connect", step: "exchange", error_class: "network" },
          extra: { verdict },
        });
        if (verdict === "check_failed") {
          // #1867 — we could not find out. "Couldn't connect" would assert a
          // disconnection the server never confirmed (a check_status 500, or
          // two dropped replies) over a connection that may well have been
          // made — the original JAVASCRIPT-REACT-32 bug, narrowed. So say
          // exactly that, and point her at the one place that shows the truth.
          setErrorHeading("Couldn't confirm");
          setErrorMessage(
            "Couldn't confirm whether Pinterest connected. Check Pinterest in Settings, and try again if it says Not connected.",
          );
        } else {
          setErrorMessage("Couldn't reach the server. Check your connection and try again.");
        }
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [code, state, errorParam, navigate, authLoading, session?.access_token]);

  return (
    <div className="flex items-center justify-center h-screen bg-background">
      <div
        className="text-center"
        style={{ maxWidth: 320, padding: 24, display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}
        role="status"
        aria-live="polite"
        data-testid="pinterest-callback"
      >
        {status === "connecting" && (
          <>
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-current border-t-transparent" style={{ color: "#5050C8" }} />
            <p style={{ fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif', fontSize: 15, color: "#54525C" }}>
              Connecting your Pinterest...
            </p>
          </>
        )}

        {status === "success" && (
          <>
            <div style={{ fontSize: 32, lineHeight: 1 }} aria-hidden="true">✓</div>
            <div style={{ fontFamily: '"DM Serif Display", serif', fontSize: 22, color: "#1C1A3E" }}>Pinterest connected</div>
            <div style={{ fontFamily: '"Instrument Serif", serif', fontStyle: "italic", fontSize: 15, color: "#5050C8" }}>
              Heading back, mama 💜
            </div>
          </>
        )}

        {status === "error" && (
          <>
            <div style={{ fontFamily: '"DM Serif Display", serif', fontSize: 22, color: "#1C1A3E" }}>{errorHeading}</div>
            <div
              style={{ fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif', fontSize: 14, color: "#54525C", lineHeight: 1.4 }}
              data-testid="pinterest-callback-error"
            >
              {errorMessage}
            </div>
            <button
              onClick={() => navigate(returnPath, { replace: true })}
              style={{
                marginTop: 8, padding: "10px 20px", backgroundColor: "#5050C8", color: "white", border: "none",
                borderRadius: 8, fontFamily: '"Plus Jakarta Sans", "Montserrat", sans-serif', fontSize: 14,
                fontWeight: 600, cursor: "pointer", minHeight: 44,
              }}
            >
              Got it, take me back
            </button>
          </>
        )}
      </div>
    </div>
  );
}
